import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    applyResolvedTheme,
    getStoredTheme,
    resolveTheme,
    saveTheme,
    systemPrefersDark,
    Theme,
} from "../utils/theme";

interface ThemeContextValue {
    theme: Theme;
    resolvedTheme: "light" | "dark";
    setTheme: (theme: Theme) => void;
}

// Deliberately NOT the useAuth()-style "throw outside a provider" pattern
// (see src/context/AuthContext.tsx): HeaderComponent renders the theme
// selector on every page, and dozens of existing page/component tests
// render that header (directly or via App) without a ThemeProvider,
// mocking only ../../context/AuthContext. Auth has real security/behaviour
// implications where failing loudly is correct; theme is a cosmetic
// preference, so a context-free render just degrades to this static
// default (System/Light, a setTheme that still persists the pick for next
// time) instead of breaking every one of those unrelated tests.
const DEFAULT_THEME_CONTEXT: ThemeContextValue = {
    theme: "system",
    resolvedTheme: "light",
    setTheme: saveTheme,
};

const ThemeContext = createContext<ThemeContextValue>(DEFAULT_THEME_CONTEXT);

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [theme, setThemeState] = useState<Theme>(() => getStoredTheme() ?? "system");
    const [prefersDark, setPrefersDark] = useState<boolean>(() => systemPrefersDark());

    // Only listens while "system" is actually selected - a live OS theme
    // change shouldn't matter (and shouldn't cost a listener) once the
    // learner has picked an explicit Light or Dark.
    useEffect(() => {
        if (theme !== "system" || typeof window.matchMedia !== "function") return undefined;
        const mql = window.matchMedia("(prefers-color-scheme: dark)");
        const handleChange = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
        mql.addEventListener("change", handleChange);
        return () => mql.removeEventListener("change", handleChange);
    }, [theme]);

    const resolvedTheme = useMemo(() => resolveTheme(theme, prefersDark), [theme, prefersDark]);

    // The inline script in public/index.html already applied a theme before
    // this mounted (see that file, and src/utils/theme.ts's own comment) -
    // this effect only needs to keep the attribute in sync with later
    // changes (an explicit pick, or a live "system" change above).
    useEffect(() => {
        applyResolvedTheme(resolvedTheme);
    }, [resolvedTheme]);

    const setTheme = useCallback((next: Theme) => {
        setThemeState(next);
        saveTheme(next);
        if (next === "system") setPrefersDark(systemPrefersDark());
    }, []);

    const value = useMemo(() => ({ theme, resolvedTheme, setTheme }), [theme, resolvedTheme, setTheme]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
    return useContext(ThemeContext);
}
